import type { CanvasPoint, OsnapPoint } from './types';

export type SimpleCircleShape = {
  kind: 'circle';
  center: CanvasPoint;
  radius: number;
};

export type SimplePolylineShape = {
  kind: 'polyline' | 'rectangle';
  points: number[];
};

export type SimpleArcShape = {
  kind: 'arc';
  center: CanvasPoint;
  radius: number;
  startAngle: number;
  endAngle: number;
};

export type SimpleDimension = {
  start: CanvasPoint;
  end: CanvasPoint;
  offset: number;
};

type PolylineBounds = {
  center: CanvasPoint;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

type DimensionLayout = {
  extensionStart: CanvasPoint;
  extensionStartOffset: CanvasPoint;
  extensionEnd: CanvasPoint;
  extensionEndOffset: CanvasPoint;
  lineMidPoint: CanvasPoint;
};

const getPolylineBounds = (points: number[]): PolylineBounds | null => {
  if (points.length < 4) return null;

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < points.length; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x === undefined || y === undefined) continue;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  return {
    center: {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    },
    minX,
    maxX,
    minY,
    maxY,
  };
};

const getSegmentProjection = (anchor: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return null;

  const projection =
    ((anchor.x - start.x) * dx + (anchor.y - start.y) * dy) / lengthSquared;

  if (projection < 0 || projection > 1) return null;

  return {
    x: start.x + projection * dx,
    y: start.y + projection * dy,
  };
};

const getCircleTangentPoints = (anchor: CanvasPoint, circle: SimpleCircleShape) => {
  const dx = anchor.x - circle.center.x;
  const dy = anchor.y - circle.center.y;
  const distanceSquared = dx * dx + dy * dy;
  const radiusSquared = circle.radius * circle.radius;

  if (distanceSquared <= radiusSquared) return [] as CanvasPoint[];

  const distance = Math.sqrt(distanceSquared);
  const angleToAnchor = Math.atan2(dy, dx);
  const tangentAngleOffset = Math.acos(circle.radius / distance);

  return [
    {
      x: circle.center.x + circle.radius * Math.cos(angleToAnchor + tangentAngleOffset),
      y: circle.center.y + circle.radius * Math.sin(angleToAnchor + tangentAngleOffset),
    },
    {
      x: circle.center.x + circle.radius * Math.cos(angleToAnchor - tangentAngleOffset),
      y: circle.center.y + circle.radius * Math.sin(angleToAnchor - tangentAngleOffset),
    },
  ];
};

export const addShapeOsnapCandidates = (
  candidates: OsnapPoint[],
  shape: SimpleCircleShape | SimplePolylineShape | SimpleArcShape,
  anchor: CanvasPoint | null,
) => {
  if (shape.kind === 'circle') {
    candidates.push(
      {
        x: shape.center.x,
        y: shape.center.y,
        type: 'CENTRE',
        distance: 0,
      },
      {
        x: shape.center.x + shape.radius,
        y: shape.center.y,
        type: 'QUADRANT',
        distance: 0,
      },
      {
        x: shape.center.x,
        y: shape.center.y + shape.radius,
        type: 'QUADRANT',
        distance: 0,
      },
      {
        x: shape.center.x - shape.radius,
        y: shape.center.y,
        type: 'QUADRANT',
        distance: 0,
      },
      {
        x: shape.center.x,
        y: shape.center.y - shape.radius,
        type: 'QUADRANT',
        distance: 0,
      },
    );

    if (anchor) {
      for (const tangentPoint of getCircleTangentPoints(anchor, shape)) {
        candidates.push({
          ...tangentPoint,
          type: 'TANGENT',
          distance: 0,
        });
      }
    }
    return;
  }

  if (shape.kind === 'arc') {
    const start = {
      x: shape.center.x + Math.cos(shape.startAngle) * shape.radius,
      y: shape.center.y + Math.sin(shape.startAngle) * shape.radius,
    };
    const end = {
      x: shape.center.x + Math.cos(shape.endAngle) * shape.radius,
      y: shape.center.y + Math.sin(shape.endAngle) * shape.radius,
    };
    const sweep = ((shape.endAngle - shape.startAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    const midpointAngle = shape.startAngle + sweep / 2;

    candidates.push(
      {
        x: shape.center.x,
        y: shape.center.y,
        type: 'CENTRE',
        distance: 0,
      },
      {
        ...start,
        type: 'END',
        distance: 0,
      },
      {
        ...end,
        type: 'END',
        distance: 0,
      },
      {
        x: shape.center.x + Math.cos(midpointAngle) * shape.radius,
        y: shape.center.y + Math.sin(midpointAngle) * shape.radius,
        type: 'MID',
        distance: 0,
      },
    );
    return;
  }

  const { points } = shape;

  for (let index = 0; index < points.length; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x === undefined || y === undefined) continue;
    candidates.push({
      x,
      y,
      type: 'END',
      distance: 0,
    });
  }

  for (let index = 0; index <= points.length - 4; index += 2) {
    const startX = points[index];
    const startY = points[index + 1];
    const endX = points[index + 2];
    const endY = points[index + 3];
    if (startX === undefined || startY === undefined || endX === undefined || endY === undefined) continue;
    const start = { x: startX, y: startY };
    const end = { x: endX, y: endY };

    candidates.push({
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2,
      type: 'MID',
      distance: 0,
    });

    if (anchor) {
      const perpendicular = getSegmentProjection(anchor, start, end);
      if (perpendicular) {
        candidates.push({
          x: perpendicular.x,
          y: perpendicular.y,
          type: 'PERP',
          distance: 0,
        });
      }
    }
  }

  const bounds = getPolylineBounds(points);
  if (!bounds) return;

  candidates.push({
    x: bounds.center.x,
    y: bounds.center.y,
    type: 'CENTRE',
    distance: 0,
  });

  candidates.push(
    {
      x: bounds.center.x,
      y: bounds.minY,
      type: 'QUADRANT',
      distance: 0,
    },
    {
      x: bounds.maxX,
      y: bounds.center.y,
      type: 'QUADRANT',
      distance: 0,
    },
    {
      x: bounds.center.x,
      y: bounds.maxY,
      type: 'QUADRANT',
      distance: 0,
    },
    {
      x: bounds.minX,
      y: bounds.center.y,
      type: 'QUADRANT',
      distance: 0,
    },
  );
};

export const addDimensionOsnapCandidates = (
  candidates: OsnapPoint[],
  dimension: SimpleDimension,
  anchor: CanvasPoint | null,
  getDimensionLayout: (start: CanvasPoint, end: CanvasPoint, offset: number) => DimensionLayout | null,
) => {
  const layout = getDimensionLayout(dimension.start, dimension.end, dimension.offset);
  if (!layout) return;

  candidates.push(
    {
      x: layout.extensionStart.x,
      y: layout.extensionStart.y,
      type: 'END',
      distance: 0,
    },
    {
      x: layout.extensionEnd.x,
      y: layout.extensionEnd.y,
      type: 'END',
      distance: 0,
    },
    {
      x: layout.extensionStartOffset.x,
      y: layout.extensionStartOffset.y,
      type: 'END',
      distance: 0,
    },
    {
      x: layout.extensionEndOffset.x,
      y: layout.extensionEndOffset.y,
      type: 'END',
      distance: 0,
    },
    {
      x: layout.lineMidPoint.x,
      y: layout.lineMidPoint.y,
      type: 'MID',
      distance: 0,
    },
  );

  if (!anchor) return;

  const dimensionSegments = [
    [layout.extensionStart, layout.extensionStartOffset],
    [layout.extensionEnd, layout.extensionEndOffset],
    [layout.extensionStartOffset, layout.extensionEndOffset],
  ] as const;

  for (const [start, end] of dimensionSegments) {
    const perpendicular = getSegmentProjection(anchor, start, end);
    if (perpendicular) {
      candidates.push({
        x: perpendicular.x,
        y: perpendicular.y,
        type: 'PERP',
        distance: 0,
      });
    }
  }
};
